import CheckIcon from "@mui/icons-material/Check";
import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Button,
  FormGroup,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { styled } from "@mui/material/styles";
import React, { useEffect, useMemo, useState } from "react";
import { config } from "../../../config";
import theme from "../../../theme";
import { ALL_SIGNUP_PLANS, AUTOMATED_PRICES, LIVE_AUTO_PRICES, LIVE_PRICES } from "./pricingData";
import MixpanelService from "../../../services/mixpanel.service";

const planFeaturesList:any  = (isYearly?: boolean, professionalTier?: "200" | "500" | "1000")=> {
  const capacityLabel =
    professionalTier === "500"
      ? "Up to 500 live attendees + 2,500 automated viewers (5x of live)"
      : professionalTier === "1000"
      ? "Up to 1,000 live attendees + 5,000 automated viewers (5x of live)"
      : "Up to 200 live attendees + 1,000 automated viewers (5x of live)";
  
  const aiCreditsLabel =
    professionalTier === "500"
      ? "1,000 AI credits — build up to 10 complete funnels"
      : professionalTier === "1000"
      ? "2,000 AI credits — build up to 20 complete funnels"
      : "500 AI credits — build up to 5 complete funnels";

  const professionalFeatures = [
    {
      title: "Everything in Launch, plus:",
      list: [
        { label: capacityLabel, tooltip: "" },
        { label: aiCreditsLabel, tooltip: "" },
        { label: "Up to 4-hour live session duration", tooltip: "" },
        { label: "Evergreen webinars — unlimited sessions on autopilot", tooltip: "" },
        { label: "EasyCRM — lead scoring, automated follow-ups & pipeline", tooltip: "" },
        { label: "Behavior-based CTAs & auto dialer", tooltip: "" },
        { label: "Sales pipeline — track every lead from registration to close", tooltip: "" },
        { label: "Custom domains, branding & advanced analytics", tooltip: "" },
        { label: "RTMP streaming + custom stream destinations", tooltip: "" },
        { label: "Priority support + dedicated account manager", tooltip: "" },
      ]
    },
  ];
  const agencyFeatures = [
    {
      title: "Everything in Pro, plus:",
      list: [
        { label: "Main account: up to 1,000 live attendees per session", tooltip: "" },
        { label: "Unlimited client sub-accounts with full Pro access", tooltip: "" },
        { label: "Your agency brand — custom domain & your client relationships", tooltip: "" },
        { label: "Set your own pricing — keep 65% of every subscription", tooltip: "" },
        { label: "Central client dashboard — manage all accounts in one place", tooltip: "" },
      ],
    },
  ];

  return{
    launch: [
      {
        title: "What's Included",
        list: [
          { label: "Go live to up to 50 attendees", tooltip: "" },
          { label: "AI Webinar Builder — 200 credits up to 2 complete funnels", tooltip: "" },
          { label: "Up to 2-hour live session duration", tooltip: "" },
          { label: "Replay funnels + behaviour-based follow-ups keep converting long after you go live", tooltip: "" },
          { label: "Sell your offer with built-in checkout — no Stripe setup needed", tooltip: "" },
          { label: "SOC 2 Type II & GDPR compliant", tooltip: "" },
        ],
      },
    ],
    professional: professionalFeatures,
    agency: agencyFeatures,
    
  }
}

// AntSwitch component from SubscriptionDetails
const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 42,
  height: 24,
  padding: 0,
  display: "flex",
  borderRadius: 50,
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 15,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(24px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    top: 1,
    "&.Mui-checked": {
      transform: "translateX(19px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "#22B060",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    width: 18,
    height: 18,
    borderRadius: 24,
    transition: theme.transitions.create(["width"], {
      duration: 200,
    }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor: "#22B060",
    boxSizing: "border-box",
  },
}));

interface PricingSectionProps {
  currentPlanId?: string;
  currentAttendees?: number;
  onPlanSelect: (planId: string) => void;
  hideTabs?: boolean;
  isSaleLive?: boolean;
  id?: string;
  subscriptionStatus?: string;
}

// Main component
const PricingSectionV2Sale: React.FC<PricingSectionProps> = ({
  currentPlanId,
  currentAttendees,
  onPlanSelect,
  hideTabs = false,
  isSaleLive,
  id,
  subscriptionStatus,
}) => {
  // Use config.isSaleLive as default if prop is not provided
  const saleLive = isSaleLive ?? config.isSaleLive;

  // State
  const [planDuration, setPlanDuration] = useState<"monthly" | "yearly">(
    "monthly"
  );
  const [tabValue, setTabValue] = useState(0);

  const [liveValue, setLiveValue] = useState("50");
  const [automatedValue, setAutomatedValue] = useState("3000");
  const [liveAutomatedValue, setLiveAutomatedValue] = useState("200");
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [professionalTier, setProfessionalTier] = useState<"200" | "500" | "1000">("200");

  // Auto-detect current plan configuration
  useEffect(() => {
    if (currentPlanId && currentAttendees) {
      // Set plan duration based on current plan
      if (currentPlanId.includes("Yearly")) {
        setPlanDuration("yearly");
      } else if (currentPlanId.includes("Monthly")) {
        setPlanDuration("monthly");
      }

      // Set attendee values based on current plan
      if (currentPlanId.includes("Live-Webinars")) {
        // Find matching live plan attendee value
        const matchingLivePlan = LIVE_PRICES.find(
          (plan) => plan.value === currentAttendees.toString()
        );
        if (matchingLivePlan) {
          setLiveValue(matchingLivePlan.value);
        }
      } else if (currentPlanId.includes("Automated-Gold")) {
        // Find matching automated plan attendee value
        const matchingAutomatedPlan = AUTOMATED_PRICES.find(
          (plan) => plan.value === currentAttendees.toString()
        );
        if (matchingAutomatedPlan) {
          setAutomatedValue(matchingAutomatedPlan.value);
        }
      } else if (currentPlanId.includes("All-In-One")) {
        // Find matching live+automated plan attendee value
        const matchingLiveAutoPlan = LIVE_AUTO_PRICES.find(
          (plan) => plan.value === currentAttendees.toString()
        );
        if (matchingLiveAutoPlan) {
          setLiveAutomatedValue(matchingLiveAutoPlan.value);
        }
        if (["200", "500", "1000"].includes(currentAttendees.toString())) {
          setProfessionalTier(currentAttendees.toString() as "200" | "500" | "1000");
        }
      }
    }
  }, [currentPlanId, currentAttendees]);

  // Handlers — use functional setState so Mixpanel matches the real transition (avoids stale planDuration).
  const applyBillingSelection = (selected: "monthly" | "annually") => {
    const next: "monthly" | "yearly" =
      selected === "annually" ? "yearly" : "monthly";
    setPlanDuration((prev) => {
      if (prev === next) return prev;
      MixpanelService.track("ua_plans_billing_toggle_clicked", { selected });
      return next;
    });
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    applyBillingSelection(e.target.checked ? "annually" : "monthly");
  };

  const getStartForFree = (
    planType: "free" | "live" | "automated" | "liveAuto"
  ) => {
    let plan, formattedPlanType, attendees;

    if (planType === "live") {
      plan =
        LIVE_PRICES.find((opt) => opt.value === liveValue) || LIVE_PRICES[0];
      formattedPlanType = "Live-Webinars";
      attendees = plan.value; // e.g., "50 Attendees" -> "50"
    } else if (planType === "automated") {
      plan =
        AUTOMATED_PRICES.find((opt) => opt.value === automatedValue) ||
        AUTOMATED_PRICES[0];
      formattedPlanType = "Automated-Gold";
      attendees = plan.value; // e.g., "200 Attendees" -> "200"
    } else if (planType === "liveAuto") {
      plan =
        LIVE_AUTO_PRICES.find((opt) => opt.value === liveAutomatedValue) ||
        LIVE_AUTO_PRICES[0];
      formattedPlanType = "All-In-One";
      attendees = plan.value; // e.g., "50 Live & 200 Automated" -> "50-200"
    } else if (planType === "free") {
      formattedPlanType = "free-trial-plan";
      onPlanSelect(formattedPlanType);
      return;
    }

    const currency = "USD";
    const duration =
      planDuration.charAt(0).toUpperCase() + planDuration.slice(1); // "Monthly" or "Yearly"

    const planId = `${formattedPlanType}-${currency}-${duration}-${attendees}`;
    onPlanSelect(planId);
  };

  const contactSalesEnterprise = () => {
    MixpanelService.track("ua_plans_contact_sales_clicked", {
      plan: "enterprise",
    });
    window.location.href = `https://calendly.com/d/ctwj-jn5-jwn/enterprise-sales-connect-growth-strategy`;
  };
  const contactSalesAgency = () => {
    MixpanelService.track("ua_plans_contact_sales_clicked", {
      plan: "agency_bundle",
    });
    window.location.href = `https://calendly.com/d/ctwf-rpp-rh6/agency-sales-connect-growth-strategy`;
  };

  // In Live Webinars card
  const liveSelected =
    LIVE_PRICES.find((opt) => opt.value === liveValue) || LIVE_PRICES[0];
  // In Automated Webinars card
  const automatedSelected =
    AUTOMATED_PRICES.find((opt) => opt.value === automatedValue) ||
    AUTOMATED_PRICES[0];
  // In Live + Automated card
  const liveAutoSelected =
    LIVE_AUTO_PRICES.find((opt) => opt.value === liveAutomatedValue) ||
    LIVE_AUTO_PRICES[0];

  const launchPlan = ALL_SIGNUP_PLANS["Live-Webinars-USD-Yearly-50"];

  const professionalPlanByTier: Record<"200" | "500" | "1000", typeof launchPlan> = {
    "200": ALL_SIGNUP_PLANS["All-In-One-USD-Yearly-200"],
    "500": ALL_SIGNUP_PLANS["All-In-One-USD-Yearly-500"],
    "1000": ALL_SIGNUP_PLANS["All-In-One-USD-Yearly-1000"],
  };
  const getPlanRankById = (planId?: string) => {
    if (!planId) return 0;
    if (planId.includes("Agency-Bundle")) return 3;
    if (planId.includes("All-In-One")) return 2;
    if (planId.includes("Live-Webinars")) return 1;
    return 0;
  };
  const currentPlanRank = getPlanRankById(currentPlanId);
  const planCardsData = [
    { key: "launch", planRank: 1, plan: launchPlan, isMostPopular: false, isProfessional: false },
    { key: "professional", planRank: 2, plan: professionalPlanByTier[professionalTier], isMostPopular: true, isProfessional: true },
    { key: "agency", planRank: 3, plan: ALL_SIGNUP_PLANS["Agency-Bundle-USD-Yearly-1000"], isMostPopular: false, isProfessional: false },
  ];

  const getDisplayPrice = (
    plan: { monthly: number; annualPlanPerMonth: number },
    durationOverride?: "monthly" | "yearly"
  ) => {
    const dur = durationOverride ?? planDuration;
    const base = dur === "yearly" ? plan.annualPlanPerMonth : plan.monthly;
    return base;
  };

  const selectPlan = (plan: any, isYearly: boolean) => {
    // const dur = durationOverride ?? planDuration;
    // const duration = dur === "yearly" ? "Yearly" : "Monthly";
    // const planId = `All-In-One-USD-${duration}-${quantity}`;
    const planId = isYearly ? plan.YearlyPlanID : plan.MonthlyPlanID;
    onPlanSelect(planId);
  };

  const getPlanDescription = (planKey: string) => {
    const d: Record<string, string> = {
      launch: "Best for small live webinars",
      professional: "Best for live, simulive & evergreen webinars",
      agency: "Best for high-volume webinars + funnels",
    };
    return d[planKey] ?? "";
  };

  const getSaveAmount = (plan: { savingsAmount?: number; annualMaxPrice?: number; annualPlanPerMonth?: number }) =>
    plan.savingsAmount ?? (plan.annualMaxPrice != null && plan.annualPlanPerMonth != null
      ? (plan.annualMaxPrice - plan.annualPlanPerMonth) * 12
      : 0);

  const PlanCard = ({
    planKey,
    planRank,
    plan,
    isMostPopular,
    isProfessional,
    currentAttendees,
  }: {
    planKey: string;
    planRank: number;
    plan: { planName: string; monthly: number; annualPlanPerMonth: number; annualMaxPrice: number; savingsAmount?: number; addons?: string[]; liveAttendees?: number; automatedAttendees?: number; attendees?: number; MonthlyPlanID?: string; YearlyPlanID?: string };
    isMostPopular?: boolean;
    isProfessional?: boolean;
    currentAttendees: number | string;
  }) => {
    console.log("planKey==>",planKey);
    
    const isYearly = planDuration === "yearly";
    const selectedPlanId = isYearly ? plan.YearlyPlanID : plan.MonthlyPlanID;
    const isCurrentCardPlan = !!currentPlanId && !!selectedPlanId && `${currentPlanId}-${currentAttendees}` === selectedPlanId;

    return (
    <Grid key={planKey} size={{ xs: 12, md: 6, lg: !hideTabs ? 3 : 3 }} maxWidth={{ xs: "350px", md: "unset" }}>
      <Paper
        elevation={0}
        sx={{
          overflow: "hidden",
          borderRadius: "12px",
          minHeight: 420,
          display: "flex",
          flexDirection: "column",
          border: "1px solid",
          borderColor: isMostPopular ? (theme.palette.primary?.main ?? "#178EFC") : "#E8EAF0",
          background: "#FFF",
          boxShadow: isMostPopular ? "0px 4px 20px rgba(23, 142, 252, 0.15)" : "0px 4px 12px 0px rgba(43, 43, 48, 0.06)",
          position: "relative",
          mt: isMostPopular ? "-35px" : 0,
          height: isMostPopular ? "calc(100% + 35px)" : "100%",
        }}
      >
        {isMostPopular && (
          <Box
            sx={{
              background: theme.palette.primary?.main ?? "#178EFC",
              color: "#fff",
              py: 1,
              textAlign: "center",
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            Most Popular
          </Box>
        )}
        <Box sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column" }}>
          {isCurrentCardPlan && subscriptionStatus === "in_trial" && (
            <Box sx={{ position: "absolute", top: isMostPopular ? 40 : 0, right: 0, background: "#22B060", color: "#fff", p: "4px 8px 4px 12px", borderRadius: "0 8px 0 8px", fontSize: "12px", fontWeight: 500, display: "flex", alignItems: "center", gap: 1, zIndex: 2 }}>
              Currently in trial
            </Box>
          )}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 1.5 }}>
            <Box>
              <Typography variant="h6" fontWeight={600} mb={0.25} align="left">
                {plan.planName}
              </Typography>
              <Typography variant="body2" color="#666" align="left" mb={2} sx={{ fontSize: "13px" }}>
                {getPlanDescription(planKey) || (plan.liveAttendees
                  ? `${plan.liveAttendees} Live${plan.automatedAttendees ? ` + ${plan.automatedAttendees?.toLocaleString()} Automated` : ""} Attendees`
                  : `${(plan.automatedAttendees ?? plan.attendees)?.toLocaleString()} Attendees`)}
              </Typography>
            </Box>
            {/* {isYearly && getSaveAmount(plan) > 0 && (
              <Typography component="span" sx={{ fontSize: "12px", color: "#666", textAlign: "right", fontWeight: 500 }}>
                Save ${getSaveAmount(plan).toLocaleString()}
              </Typography>
            )} */}
          </Box>
          {isProfessional && (
            <Box sx={{ display: "inline-flex", gap: 1, mb: 2 }}>
              {(["200", "500", "1000"] as const).map((tier) => (
                <Button
                  key={tier}
                  onClick={() => {
                    if (professionalTier === tier) return;
                    MixpanelService.track("ua_plans_attendee_tier_clicked", {
                      tier: Number(tier),
                      plan: plan.planName,
                    });
                    setProfessionalTier(tier);
                  }}
                  sx={{
                    textTransform: "none",
                    minWidth: 74,
                    height: 40,
                    px: 2,
                    py: 0,
                    fontSize: "14px",
                    fontWeight: 600,
                    lineHeight: 1,
                    borderRadius: "10px",
                    border: "1px solid #2B2B30",
                    color: professionalTier === tier ? "#fff !important" : "#111827 !important",
                    bgcolor: professionalTier === tier ? "#000000" : "#F3F4F6",
                    "&:hover": {
                      color: professionalTier === tier ? "#fff !important" : "#111827 !important",
                      bgcolor: professionalTier === tier ? "#000000" : "#E5E7EB",
                      borderColor: "#2B2B30 !important",
                      boxShadow: "none !important",
                    },
                  }}
                >
                  {tier}
                </Button>
              ))}
            </Box>
          )}
          {/* {(planKey === "50" || planKey === "200" || planKey === "500" || planKey === "1000") && (
            <Typography variant="body2" color="#000" sx={{ fontSize: "15px", mb: 3, mt: 2, fontWeight: 500 }}>
              {planKey === "50" && "50 Live Attendees"}
              {planKey === "200" && "200 Live + 1,000 Automated Attendees"}
              {planKey === "500" && "500 Live + 2,500 Automated Attendees"}
              {planKey === "1000" && "1,000 Live + 5,000 Automated Attendees"}
            </Typography>
          )}
          {(planKey === "3000" || planKey === "6000" || planKey === "10000") && (
            <Typography variant="body2" color="#000" sx={{ fontSize: "15px", mb: 3, mt: 2, fontWeight: 500 }}>
              {planKey === "3000" && "3,000 Automated Attendees"}
              {planKey === "6000" && "6,000 Automated Attendees"}
              {planKey === "10000" && "10,000 Automated Attendees"}
            </Typography>
          )} */}
          {isYearly && (
            <Typography component="span" fontSize={20} color="rgba(0, 0, 0, 0.314)" fontWeight={500} mr={1} sx={{ textDecoration: "line-through" }}>
              ${plan.annualMaxPrice}
            </Typography>
          )}
          <Typography variant="h5" fontWeight={700} color="#222" display="flex" alignItems="baseline" mb={0.5}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
            <span style={{ display: "flex", alignItems: "baseline", fontSize: "2rem" }}>
              $<Typography component="span" fontWeight="inherit" fontSize={34}>{getDisplayPrice(plan, planDuration)}</Typography>
            </span>
            <Typography component="span" fontSize={16} color="#666666" fontWeight={400}>/month</Typography>
            </Box>
          </Typography>
          {isYearly && (
              <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: "12px", mb: 1.5 }}>
                Billed Annually — You're saving ${getSaveAmount(plan).toLocaleString()} /yr
              </Typography>
          )}
          {!isYearly && planKey === "launch" && (
              <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: "12px", mb: 1.5 }}>
                Upgrade to annual - pay for 10 months only
              </Typography>
          )}
          {!isYearly && (planKey === "professional" || planKey === "agency") && (
              <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: "12px", mb: 1.5 }}>
                Switch to annual billing — pay for 10 months + free add-ons.
              </Typography>
          )}
            {isCurrentCardPlan && (subscriptionStatus === "in_trial" || subscriptionStatus === "is_trial") ? (
              <Button variant="contained" size="large" onClick={() => selectPlan(plan, isYearly)} sx={{ width: "100%", mb: 1, bgcolor: "#178EFC", color: "#fff", "&:hover": { bgcolor: "#066fcffa" } }}>
                Activate now
              </Button>
            ) : isCurrentCardPlan ? (
              <Button variant="outlined" size="large" disabled sx={{ width: "100%", mb: 1, borderColor: "#2B2B30", color: "#2B2B30" }}>
                Current plan
              </Button>
            ) : planKey === "agency" ? <Button variant="outlined" size="large" onClick={() => contactSalesAgency()} sx={{ width: "100%", mb: 1, color: "#2B2B30", borderColor: "#2B2B30", "&:hover": { bgcolor: "#2B2B30", color: "#fff" } }}>
              Contact Sales
            </Button> : (
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  if (
                    planKey === "launch" &&
                    currentAttendees &&
                    currentPlanRank > planRank
                  ) {
                    MixpanelService.track("ua_plans_downgrade_clicked", {
                      target_plan: "launch",
                      current_plan: currentPlanId ?? "",
                    });
                  }
                  selectPlan(plan, isYearly);
                }}
                sx={{ width: "100%", mb: 1, bgcolor: "#2B2B30", color: "#fff", "&:hover": { bgcolor: "#1a1a1a" } }}
              >
                {!currentAttendees ? "Start 7-Day Trial" : currentPlanRank > planRank ? "Downgrade plan" : "Upgrade plan"}
              </Button>
            )}
          <Box sx={{ mt: 2 }}>
            {(planFeaturesList(isYearly, professionalTier)?.[planKey] ?? []).map((section: any, sectionIndex: any) => (
              <Box key={sectionIndex} sx={{ mb: sectionIndex === 0 ? 2 : 0 }}>
                <Typography variant="body2" fontWeight={700} color="#222" mb={1.5}>{section.title}</Typography>
                <Box>
                  {section.list.map((feature: any, featureIndex: any) => (
                    <Box key={featureIndex} sx={{ mb: 0.85 }}>
                      <Typography variant="body2" color="#4a4a4a" sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 14 }}>
                        <Box sx={{color: theme.palette.primary?.main, fontWeight: 600, fontSize: 16}}>✓</Box> <span>{feature.label}</span>
                        {!!feature?.tooltip && (
                          <Tooltip arrow title={feature.tooltip || ""}>
                            <InfoIcon sx={{ fontSize: 14, color: "#999", cursor: "help" }} />
                          </Tooltip>
                        )}
                      </Typography>
                      {feature?.tag ? (
                        <Box component="span" sx={{ display: "inline-block", mt: 0.25, ml: 2.5, px: 1, py: 0.25, bgcolor: "#d4edda", color: "#155724", fontSize: 12, fontWeight: 600, borderRadius: 10, textTransform: "lowercase" }}>
                          {feature.tag}
                        </Box>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>
    </Grid>
  );
  };

  // Memoized FEATURES array
  const FEATURES = useMemo(
    () => [
      { section: "Core Webinar Features" },
      { label: "AI Webinar Builder", plans: ["Included", "Included", "Included"] },
      {
        label: "AI webinar funnels",
        plans: [
          "2 funnels (200 credits)",
          "5-20 funnels (500-2,000 credits)",
          "Up to 20",
        ],
      },
      { label: "Live webinars", plans: ["Included", "Included", "Included"] },
      { label: "Automated webinars", plans: ["-", "Included", "Included"] },
      { label: "Simulive / Evergreen", plans: ["-", "Included", "Included"] },
      { label: "Live session duration", plans: ["2 hrs", "4 hrs", "4 hrs"] },
      { label: "Active webinars", plans: ["Up to 5 live", "Unlimited", "Unlimited"] },
      { label: "Live attendees per session", plans: ["50", "200-1,000", "1,000"] },
      {
        label: "Automated viewers per session",
        plans: ["-", "1,000-5,000 (5x of live)", "Up to 5,000 (5x of live)"],
      },
      { label: "Stream to YouTube / RTMP", plans: ["-", "Included", "Included"] },
      { label: "Multistream (LinkedIn, YT, FB)", plans: ["Included", "Included", "Included"] },

      { section: "Engagement Features" },
      { label: "Live chat", plans: ["Included", "Included", "Included"] },
      { label: "Offers, polls & Q&A", plans: ["Included", "Included", "Included"] },
      { label: "Green room / backstage", plans: ["Included", "Included", "Included"] },
      { label: "Attendee spotlight", plans: ["Included", "Included", "Included"] },
      { label: "Paid webinars", plans: ["-", "Included", "Included"] },
      { label: "Behavior-based CTAs", plans: ["-", "Included", "Included"] },

      { section: "CRM & Automation" },
      { label: "Built-in Sales CRM (EasyCRM)", plans: ["-", "Included", "Included"] },
      { label: "Lead capture & lists", plans: ["-", "Included", "Included"] },
      { label: "Lead scoring & segmentation", plans: ["-", "Included", "Included"] },
      { label: "Automated email follow-ups", plans: ["-", "Included", "Included"] },
      { label: "Automated call follow-ups", plans: ["-", "Included", "Included"] },
      { label: "Auto dialer", plans: ["-", "Included", "Included"] },
      { label: "Sales pipeline & activity timeline", plans: ["-", "Included", "Included"] },

      { section: "Agency Features" },
      { label: "Unlimited client accounts", plans: ["-", "-", "Included"] },
      { label: "White-label rooms & domains", plans: ["-", "-", "Included"] },
      { label: "Set your own pricing", plans: ["-", "-", "Included"] },
      { label: "Central client dashboard", plans: ["-", "-", "Included"] },
      { label: "65% recurring revenue share", plans: ["-", "-", "Included"] },

      { section: "Integrations & API" },
      { label: "Essential CRM integrations", plans: ["Included", "Included", "Included"] },
      { label: "All CRM integrations", plans: ["-", "Included", "Included"] },
      { label: "Zapier", plans: ["Included", "Included", "Included"] },
      { label: "API access", plans: ["-", "-", "-"] },

      { section: "Security & Compliance" },
      { label: "SOC 2 compliance", plans: ["Included", "Included", "Included"] },
      { label: "GDPR compliance", plans: ["Included", "Included", "Included"] },
      { label: "SSO login", plans: ["-", "-", "-"] },
      { label: "Two-factor authentication (2FA)", plans: ["Included", "Included", "Included"] },

      { section: "Support & Extras" },
      { label: "Support level", plans: ["Standard", "Priority", "Priority + Manager"] },
      { label: "Live session monitoring", plans: ["-", "-", "-"] },
      { label: "Dedicated account manager", plans: ["-", "-", "Included"] },
      { label: "Priority SLA", plans: ["-", "-", "-"] },
      { label: "Early roadmap access", plans: ["-", "-", "-"] },
    ],
    [liveSelected, automatedSelected, liveAutoSelected]
  );

  // Helper function to check if this is the current plan
  const isCurrentPlan = (
    planType: "free" | "live" | "automated" | "liveAuto" | "agency"
  ) => {
    if (!currentPlanId) return false;

    if (planType === "free") {
      // For free plan, only check the plan ID
      return currentPlanId.includes("free-trial-plan");
    }
    if (planType === "agency") {
      return currentPlanId.includes("Agency-Bundle");
    }

    // Check if the current plan ID contains the plan type
    let planTypeMatch = false;
    let attendeeMatch = false;
    let billingFrequencyMatch = false;

    // Check billing frequency match
    const currentBillingFrequency =
      planDuration === "yearly" ? "Yearly" : "Monthly";
    billingFrequencyMatch = currentPlanId.includes(currentBillingFrequency);

    if (planType === "live") {
      planTypeMatch = currentPlanId.includes("Live-Webinars");
      attendeeMatch = currentAttendees === parseInt(liveValue);
    } else if (planType === "automated") {
      planTypeMatch = currentPlanId.includes("Automated-Gold");
      attendeeMatch = currentAttendees === parseInt(automatedValue);
    } else if (planType === "liveAuto") {
      planTypeMatch = currentPlanId.includes("All-In-One");
      attendeeMatch = currentAttendees === parseInt(liveAutomatedValue);
    }

    return planTypeMatch && attendeeMatch && billingFrequencyMatch;
  };

  // Helper function to check if this is the current trial plan
  // const isCurrentTrialPlan = (
  //   planType: "free" | "live" | "automated" | "liveAuto"
  // ) => {
  //   // Check if subscription is in trial status
  //   if (subscriptionStatus !== "in_trial" || !currentPlanId) return false;

  //   if (planType === "free") {
  //     return currentPlanId.includes("free-trial-plan");
  //   }

  //   // For trial plans, check if the current plan matches the plan type, attendee count, and billing frequency
  //   let planTypeMatch = false;
  //   let attendeeMatch = false;
  //   let billingFrequencyMatch = false;

  //   // Check billing frequency match
  //   const currentBillingFrequency =
  //     planDuration === "yearly" ? "Yearly" : "Monthly";
  //   billingFrequencyMatch = currentPlanId.includes(currentBillingFrequency);

  //   if (planType === "live") {
  //     planTypeMatch = currentPlanId.includes("Live-Webinars");
  //     attendeeMatch = currentAttendees === parseInt(liveValue);
  //   } else if (planType === "automated") {
  //     planTypeMatch = currentPlanId.includes("Automated-Gold");
  //     attendeeMatch = currentAttendees === parseInt(automatedValue);
  //   } else if (planType === "liveAuto") {
  //     planTypeMatch = currentPlanId.includes("All-In-One");
  //     attendeeMatch = currentAttendees === parseInt(liveAutomatedValue);
  //   }

  //   return planTypeMatch && attendeeMatch && billingFrequencyMatch;
  // };

  // Render main component
  return (
    <>
      <Box className="v3-pricing-plan-table" sx={{ my: 4 }} id={id}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap="16px"
          sx={{ mb: 3 }}
        >
          {!hideTabs && (
            <Tabs
              value={tabValue}
              onChange={(_, newValue) => setTabValue(newValue)}
              centered
            >
              <Tab
                label="Plans"
                sx={{
                  textTransform: "capitalize",
                  fontSize: { xs: "14px", sm: "16px" },
                  padding: { xs: "6px 12px" },
                }}
                onClick={() => {
                    MixpanelService.track("ua_plans_tab_clicked", { 'Click "Plans" tab': 'screen: "tab palans"' }); 
                }}
              />
              <Tab
                label="Compare Plans"
                sx={{
                  textTransform: "capitalize",
                  fontSize: { xs: "14px", sm: "16px" },
                  padding: { xs: "6px 12px" },
                }}
                onClick={() => {
                  MixpanelService.track("ua_plans_tab_clicked", { 'Click "Compare Plans" tab': 'screen: "tab compare_plans"' });
                }}
              />
            </Tabs>
          )}

          {tabValue === 1 ? (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap="16px"
            ml="auto"
            mb={2}
          >
            {saleLive ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "stretch",
                  background: "#F5F5F5",
                  borderRadius: "100px",
                  padding: "4px",
                  border: "1px solid #E0E0E0",
                  position: "relative",
                  // width: "fit-content",
                  minWidth: "260px",
                  whiteSpace: "nowrap",
                }}
              >
                
                <Box
                  sx={{
                    flex: 1,
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    zIndex: 2,
                    cursor: "pointer",
                    py: 1.5,
                    px: 2,
                    backgroundColor:
                      planDuration === "monthly" ? "#000000" : "transparent",
                    borderRadius: "100px",
                    transition: "background-color 0.3s ease",
                  }}
                  onClick={() => applyBillingSelection("monthly")}
                >
                  <Typography
                    sx={{
                      color: planDuration === "monthly" ? "#FFFFFF" : "#000000",
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: 1.2,
                    }}
                  >
                    Monthly - 40% OFF (first 3 months)
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    zIndex: 2,
                    cursor: "pointer",
                    py: 1.5,
                    px: 2,
                    backgroundColor:
                      planDuration === "yearly" ? "#000000" : "transparent",
                    borderRadius: "100px",
                    transition: "background-color 0.3s ease",
                  }}
                  onClick={() => applyBillingSelection("annually")}
                >
                  <Typography
                    sx={{
                      color: planDuration === "yearly" ? "#FFFFFF" : "#000000",
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: 1.2,
                    }}
                  >
                    Yearly - 50% OFF
                  </Typography>
                </Box>
              </Box>
            ) : (
              <FormGroup>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "center" }}
                >
                  <Typography
                    color={planDuration === "monthly" ? "#22B060" : "#000000"}
                    fontWeight="500"
                  >
                    Monthly
                  </Typography>
                  <AntSwitch
                    checked={planDuration === "yearly"}
                    onChange={handleDurationChange}
                    inputProps={{ "aria-label": "plan duration toggle" }}
                  />
                  <Typography
                    display="flex"
                    color={planDuration === "yearly" ? "#22B060" : "#000000"}
                    alignItems="center"
                    gap={1}
                    fontWeight="500"
                  >
                    Yearly{" "}
                    <Typography
                      component="span"
                      color="#2B2B30"
                      fontSize={{ xs: "12px", sm: "13px" }}
                      fontWeight={500}
                    >
                      (2 Months Free)
                    </Typography>
                  </Typography>
                </Stack>
              </FormGroup>
            )}
          </Box>
          ) : null}
        </Box>
        {tabValue === 0 ? (
          <Box className="ew-plan-boxes-con" mt={2}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "stretch",
                background: "#F5F5F5",
                borderRadius: "100px",
                padding: "4px",
                border: "1px solid #E0E0E0",
                mb: 4,
              }}
            >
              <Box
                sx={{
                  cursor: "pointer",
                  py: 1,
                  px: 2.5,
                  borderRadius: "100px",
                  backgroundColor: planDuration === "monthly" ? "#000000" : "transparent",
                  transition: "background-color 0.3s ease",
                }}
                onClick={() => applyBillingSelection("monthly")}
              >
                <Typography sx={{ color: planDuration === "monthly" ? "#FFFFFF" : "#000000", fontWeight: 500, fontSize: "14px", lineHeight: 1.2 }}>
                  Monthly
                </Typography>
              </Box>
              <Box
                sx={{
                  cursor: "pointer",
                  py: 1,
                  px: 2.5,
                  borderRadius: "100px",
                  backgroundColor: planDuration === "yearly" ? "#000000" : "transparent",
                  transition: "background-color 0.3s ease",
                }}
                onClick={() => applyBillingSelection("annually")}
              >
                <Typography sx={{ color: planDuration === "yearly" ? "#FFFFFF" : "#000000", fontWeight: 500, fontSize: "14px", lineHeight: 1.2 }}>
                  Annually
                </Typography>
              </Box>
            </Box>
            <Grid container justifyContent="center">
              <Grid
                size={{ xs: 12 }}
                container
                spacing={2}
                width="100%"
                justifyContent={{ xs: "center", md: "unset" }}
                pt={5}
                sx={{ "--Grid-parent-columns": 12 } as any}
              >
              {planCardsData.map(({ key: planKey, planRank, plan }, index) => (
                <PlanCard key={planKey} planKey={planKey} planRank={planRank} plan={plan} isMostPopular={index === 1} isProfessional={index === 1} currentAttendees={Number(currentAttendees)} />
              ))}
              <Grid
                size={{ xs: 12, md: 6, lg: !hideTabs ? 3 : 3 }}
                maxWidth={{ xs: "380px", md: "unset" }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    minHeight: 420,
                    display: "flex",
                    flexDirection: "column",
                    border: "1px solid var(--Stroke-color, #E8EAF0)",
                    background: "var(--White, #FFF)",
                    boxShadow: "0px 4px 12px 0px rgba(43, 43, 48, 0.06)",
                    height: "100%",
                    position: "relative",
                  }}
                >
                  <Typography variant="h6" fontWeight={600} mb={1} align="left">
                    Enterprise Tier
                    {/* <Box
                      component="span"
                      sx={{
                        fontSize: 13,
                        color: "#222",
                        ml: 1,
                        fontWeight: 400,
                      }}
                    >
                      Now with
                      <Box
                        component="span"
                        sx={{
                          color: "primary.main",
                          fontWeight: 600,
                          fontSize: 14,
                        }}
                      >
                        {" "}
                        AI
                      </Box>
                    </Box> */}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="#666"
                    mb={2}
                    align="left"
                    sx={{ fontSize: "14px" }}
                  >
                    Custom features, dedicated success manager, built to scale
                    results.
                  </Typography>

                  {/* Attendee Selection */}
                  {/* <Box
                    width="100%"
                    mb={3}
                    display="flex"
                    flexDirection="column"
                  >
                    <Typography
                      variant="body2"
                      color="#666"
                      mb={1}
                      sx={{ fontSize: "14px", alignSelf: "flex-start" }}
                    >
                      Attendees
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        background: "#FFF",
                        width: "fit-content",
                        borderRadius: "6px",
                        p: 0.5,
                        gap: 0.5,
                        border: "1px solid #E4E4E7",
                      }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          py: 1,
                          px: 2,
                          fontSize: "14px",
                          fontWeight: 500,
                          borderRadius: "4px",
                          textTransform: "none",
                          background: "#3F3F3F",
                          color: "#fff",
                          "&:hover": {
                            background: "#3F3F3F",
                          },
                        }}
                      >
                        Upto 50,000
                      </Button>
                    </Box>
                  </Box> */}

                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color="#222"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mb={2}
                    fontSize="32px"
                  >
                    Get a quote
                  </Typography>

                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => contactSalesEnterprise()}
                    sx={{
                      width: "100%",
                      mb: 1,
                      border: "1px solid #2B2B30",
                      color: "#2B2B30",
                      background: "#fff",
                      "&:hover": {
                        background: "#2B2B30",
                        color: "#fff",
                        border: "1px solid #2B2B30",
                      },
                    }}
                  >
                    Contact Sales
                  </Button>
                  <Box sx={{ mt: 2 }}>
                    {[
                      {
                        title: "Everything in Growth, plus:",
                        list: [
                          { label: "Unlimited live, simulive & evergreen webinars", tooltip: "" },
                          { label: "AI webinar funnels (up to 20)", tooltip: "Each AI webinar funnel includes a registration page, live or automated webinar, email follow-ups, and conversion tracking." },
                          {
                            label: "Built-in Sales CRM (EasyCRM)",
                            tooltip: "",
                          },
                          {
                            label: "Sub-accounts & team access",
                            tooltip: "",
                          },
                          {
                            label: "Priority support + SLA options",
                            tooltip: "",
                          },
                        ],
                      },
                    ].map((section, sectionIndex) => (
                      <Box
                        key={sectionIndex}
                        sx={{ mb: sectionIndex === 2 ? 0 : 2 }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="#222"
                          mb={1.5}
                        >
                          {section.title}
                        </Typography>
                        <Box>
                          {section.list.map((feature, featureIndex) => (
                            <Typography
                              key={featureIndex}
                              variant="body2"
                              color="#4a4a4a"
                              sx={{
                                mb: 0.85,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                fontSize: 14,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "baseline",
                                  gap: 1,
                                }}
                              >
                                <Box sx={{color: theme.palette.primary?.main, fontWeight: 600, fontSize: 16}}>✓</Box> <span>{feature.label}</span>
                              </Box>
                              {!!feature?.tooltip && (
                                <Tooltip arrow title={feature.tooltip || ""}>
                                  <InfoIcon
                                    sx={{
                                      fontSize: 14,
                                      color: "#999",
                                      cursor: "help",
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Grid>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              background: "#fff",
              borderRadius: 3,
              boxShadow: "0 2px 8px 0 rgba(0,0,0,0.03)",
            }}
            overflow="visible"
            position="relative"
          >
            <Box
              position="sticky"
              top={0}
              zIndex={1}
              display="flex"
              width="100%"
              minWidth={1100}
              border="1px solid #E8EAF0"
              borderRadius="12px"
              bgcolor="#ffffff"
            >
              {/* Info column */}
              <Box
                sx={{
                  minWidth: 240,
                  background:
                    "linear-gradient(135deg, #4F8CFF 0%, #6DC7FF 100%)",
                  color: "#fff",
                  borderTopLeftRadius: 12,
                  borderBottomLeftRadius: 12,
                  p: 4,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="h5" fontWeight={700} mb={1}>
                  Compare plans
                </Typography>
              </Box>
              {/* Plan columns */}
              {[
                {
                  name: "Launch",
                  color: theme.palette.primary.main,
                  price: saleLive
                    ? planDuration === "yearly"
                      ? Math.round((liveSelected.yearlySalePrice ?? 0) * 0.5)
                      : Math.round((liveSelected.monthly ?? 0) * 0.6)
                    : planDuration === "yearly"
                      ? liveSelected.annual
                      : liveSelected.monthly,
                  priceLabel: planDuration === "yearly" ? "/month" : "/month",
                  dropdown: (
                    <Select
                      sx={{ width: "90%", mb: 1, mt: 1, background: "#fff" }}
                      size="small"
                      value={liveValue}
                      onChange={(e) => setLiveValue(e.target.value)}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            "& ul": { p: "4px" },
                            "& li": { p: "8px 16px", borderRadius: "4px" },
                          },
                        },
                      }}
                    >
                      {LIVE_PRICES.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  ),
                  button: (
                    <Button
                      variant="contained"
                      size="large"
                      color="primary"
                      sx={{
                        mt: 1,
                        pointerEvents: isCurrentPlan("live") ? "none" : "auto",
                        bgcolor: isCurrentPlan("live")
                          ? "rgba(79, 140, 255, 0.1)"
                          : "#2B2B30",
                        color: isCurrentPlan("live") ? "#2B2B30" : "#fff",
                        border: isCurrentPlan("live")
                          ? "1px solid #4F8CFF"
                          : undefined,
                        boxShadow: isCurrentPlan("live") ? "none" : undefined,
                        "&:hover": {
                          bgcolor: isCurrentPlan("live")
                            ? "rgba(79, 140, 255, 0.2)"
                            : "#2B2B30",
                          boxShadow: isCurrentPlan("live") ? "none" : undefined,
                        },
                      }}
                      onClick={() => setTabValue(0)}
                    >
                      {isCurrentPlan("live") ? "Current Plan" : "Upgrade"}
                    </Button>
                  ),
                },
                {
                  name: "Professional",
                  color: theme.palette.primary.main,
                  price: saleLive
                    ? planDuration === "yearly"
                      ? Math.round(
                        (liveAutoSelected.yearlySalePrice ?? 0) * 0.5
                      )
                      : Math.round((liveAutoSelected.monthly ?? 0) * 0.6)
                    : planDuration === "yearly"
                      ? liveAutoSelected.annual
                      : liveAutoSelected.monthly,
                  priceLabel: planDuration === "yearly" ? "/month" : "/month",
                  dropdown: (
                    <Select
                      sx={{ width: "90%", mb: 1, mt: 1, background: "#fff" }}
                      size="small"
                      value={liveAutomatedValue}
                      onChange={(e) => setLiveAutomatedValue(e.target.value)}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            "& ul": { p: "4px" },
                            "& li": { p: "8px 16px", borderRadius: "4px" },
                          },
                        },
                      }}
                    >
                      {LIVE_AUTO_PRICES.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  ),
                  button: (
                    <Button
                      variant="contained"
                      size="large"
                      color="primary"
                      sx={{
                        mt: 1,
                        pointerEvents: isCurrentPlan("liveAuto")
                          ? "none"
                          : "auto",
                        bgcolor: isCurrentPlan("liveAuto")
                          ? "rgba(79, 140, 255, 0.1)"
                          : "#2B2B30",
                        color: isCurrentPlan("liveAuto") ? "#2B2B30" : "#fff",
                        border: isCurrentPlan("liveAuto")
                          ? "1px solid #4F8CFF"
                          : undefined,
                        boxShadow: isCurrentPlan("liveAuto")
                          ? "none"
                          : undefined,
                        "&:hover": {
                          bgcolor: isCurrentPlan("liveAuto")
                            ? "rgba(79, 140, 255, 0.2)"
                            : "#2B2B30",
                          boxShadow: isCurrentPlan("liveAuto")
                            ? "none"
                            : undefined,
                        },
                      }}
                      onClick={() => setTabValue(0)}
                    >
                      {isCurrentPlan("liveAuto") ? "Current Plan" : "Upgrade"}
                    </Button>
                  ),
                },
                {
                  name: "Agency",
                  color: theme.palette.primary.main,
                  price: null,
                  priceLabel: "",
                  dropdown: (
                    <Typography
                      fontWeight={500}
                      mt="13px"
                      mb={1}
                      fontSize={14}
                      textAlign="center"
                    >
                      Agency
                    </Typography>
                  ),
                  button: (
                    <Button
                      variant="contained"
                      size="large"
                      color="primary"
                      sx={{
                        mt: 1,
                        pointerEvents: isCurrentPlan("agency") ? "none" : "auto",
                        bgcolor: isCurrentPlan("agency")
                          ? "rgba(79, 140, 255, 0.1)"
                          : "#2B2B30",
                        color: isCurrentPlan("agency") ? "#2B2B30" : "#fff",
                        border: isCurrentPlan("agency")
                          ? "1px solid #4F8CFF"
                          : undefined,
                        boxShadow: isCurrentPlan("agency") ? "none" : undefined,
                        "&:hover": {
                          bgcolor: isCurrentPlan("agency")
                            ? "rgba(79, 140, 255, 0.2)"
                            : "#2B2B30",
                          boxShadow: isCurrentPlan("agency") ? "none" : undefined,
                        },
                      }}
                      onClick={() => contactSalesAgency()}
                    >
                      {isCurrentPlan("agency") ? "Current Plan" : "Contact Sales"}
                    </Button>
                  ),
                },
                {
                  name: "Enterprise",
                  color: theme.palette.primary.main,
                  price: null,
                  priceLabel: "",
                  dropdown: (
                    <Typography
                      fontWeight={500}
                      mt="13px"
                      mb={1}
                      fontSize={14}
                      textAlign="center"
                    >
                      Up to <br />
                      <span
                        style={{
                          fontSize: 24,
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                        }}
                      >
                        500K
                      </span>
                      <br />
                      attendees
                    </Typography>
                  ),
                  button: (
                    <Button
                      variant="contained"
                      size="large"
                      color="primary"
                      sx={{
                        mt: 1,
                        bgcolor: "#2B2B30",
                        color: "#fff",
                        border: "1px solid #2B2B30",
                        "&:hover": {
                          bgcolor: "#2B2B30",
                          color: "#fff",
                          border: "1px solid #2B2B30",
                        },
                      }}
                      onClick={() => contactSalesEnterprise()}
                    >
                      Upgrade
                    </Button>
                  ),
                },
              ]
                .filter((plan) => plan.name !== "Enterprise")
                .map((plan, idx, arr) => (
                <Box
                  key={plan.name}
                  position="relative"
                  sx={{
                    flex: 1,
                    minWidth: 220,
                    borderRight: idx < arr.length - 1 ? "1px solid #E8EAF0" : "none",
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                  }}
                >
                  {plan.name === "Live + Automated" && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: -24,
                        left: 0,
                        width: "100%",
                        height: 24,
                        background: theme.palette.primary.main,
                        borderTopLeftRadius: "12px",
                        borderTopRightRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 2,
                      }}
                    >
                      <Typography
                        sx={{ color: "#fff", fontWeight: 500, fontSize: 14 }}
                      >
                        Recommended
                      </Typography>
                    </Box>
                  )}
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    color={plan.color}
                    mb={1}
                  >
                    {plan.name}
                  </Typography>
                  {/* {plan.price === "custom" && <Typography variant="h5" fontWeight={700} fontSize="16px" mb='20px' color={theme.palette.primary.main} textAlign="center">
                                        Contact sales for a quote
                                    </Typography>}
                                    {plan.price !== null && plan.price !== "custom" ? (
                                        <Typography variant="h5" fontWeight={700} color="#222" m={0} mx={0} display="flex" alignItems="center">
                                            {saleLive ? (
                                                <>
                                                    <span style={{ textDecoration: 'line-through', color: '#999', marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                                                        $<Typography component="span" fontWeight="inherit" fontSize={28}>
                                                            {plan.name === 'Live Webinars'
                                                                ? (planDuration === 'yearly' ? liveSelected.yearlySalePrice : liveSelected.monthly)
                                                                : plan.name === 'Automated'
                                                                    ? (planDuration === 'yearly' ? automatedSelected.yearlySalePrice : automatedSelected.monthly)
                                                                    : (planDuration === 'yearly' ? liveAutoSelected.yearlySalePrice : liveAutoSelected.monthly)
                                                            }
                                                        </Typography>
                                                    </span>
                                                    <span style={{ display: 'flex', alignItems: 'center' }}>
                                                        $<Typography component="span" fontWeight="inherit" fontSize={28}>
                                                            {plan.price}
                                                        </Typography>
                                                    </span>
                                                </>
                                            ) : (
                                                <span style={{ display: 'flex', alignItems: 'center' }}>$<Typography component="span" fontWeight="inherit" fontSize={28}>{plan.price}</Typography></span>
                                            )}
                                            <Typography component="span" fontSize={14} color="#666666" fontWeight={400} ml={1}>{plan.priceLabel}</Typography>
                                        </Typography>
                                    ) : ""} */}
                  {/* 
                                    {plan.dropdown && plan.price !== null && plan.dropdown}

                                    {plan.price === null && plan.dropdown} */}
                  {plan.button}
                </Box>
              ))}
            </Box>
            {/* Features Table */}
            <Box
              sx={{
                width: "100%",
                minWidth: 1100,
                background: "#fff",
                borderRadius: "0 0 12px 12px",
                boxShadow: "0 2px 8px 0 rgba(0,0,0,0.03)",
                mt: 0,
              }}
            >
              {FEATURES.map(
                (feature, i) =>
                  feature.section ? (
                    <Box
                      key={feature.section}
                      sx={{
                        width: "100%",
                        px: 4,
                        pt: "14px",
                        pb: "10px",
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                        fontSize: 22,
                        background: "transparent",
                        letterSpacing: 0.2,
                        borderTop: "1px solid #E8EAF0",

                        "& + div": {
                          borderTop: "none",
                        },
                      }}
                    >
                      {feature.section}
                    </Box>
                  ) : (
                    <Box
                      key={feature.label}
                      display="flex"
                      alignItems="center"
                      sx={{
                        borderTop: i === 0 ? "none" : "1px solid #E8EAF0",
                        py: "10px",
                        px: 0,
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: 240,
                          pl: 4,
                          color: "#2B2B30",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          maxWidth: "20%",
                          width: "100%",
                        }}
                      >
                        {feature.label}
                        {/* {feature.info && (
                                                <Tooltip arrow title={feature.info}>
                                                    <Box component="span" sx={{ display: 'inline-flex', ml: 1 }}>
                                                        <InfoIcon />
                                                    </Box>
                                                </Tooltip>
                                            )} */}
                      </Box>
                      {Array.isArray(feature.plans) &&
                        feature.plans.map((val, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              flex: 1,
                              minWidth: 220,
                              textAlign: "center",
                              fontSize: 14,
                              color: "#333",
                              fontWeight: 500,
                            }}
                          >
                            {val === "Included" ? (
                              <CheckIcon sx={{ color: "#333333", fontSize: 19 }} />
                            ) : (
                              val
                            )}
                          </Box>
                        ))}
                    </Box>
                  )
              )}
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default PricingSectionV2Sale;
